//------------------------------------------------------------------------------
// BFA10103G 共架申込 機器設備 - 地図
//------------------------------------------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import '@arcgis/map-components/components/arcgis-map';
import '@arcgis/map-components/components/arcgis-scale-bar';
import {
  ArcgisMap,
  ArcgisScaleBar,
} from "@arcgis/map-components-react"; // 地図ラッパー (React 19 なら不要)
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Handles from '@arcgis/core/core/Handles';
import '@esri/calcite-components/dist/components/calcite-action-group';
import { CalciteActionGroup } from '@esri/calcite-components-react'

import appConfig from '@/config';
import MapWheelSetting, { enableWheelZoom } from '@/components/map-wheel';
import MapActionBar from '@/components/map-action-bar';
import MapActionButton from '@/components/map-action';
import MapActionLayout from '@/components/map-action-layout';
import MapCompassButton from '@/components/map-compass';
import MapSearchButton from '@/components/map-search';
import MapZoomButton from '@/components/map-zoom';
import { usePopupPortal, PopupPortal } from '@/components/map-popup';
import mapUtils from '@/utils/map-utils';
import PoleItem from '@/utils/pole-item';
import popup from './popup';
import { drawPoles } from './draw-poles';
import { drawSetubi } from './draw-setubi';
import { rangeSelectSketchModel, rangeSelectHandler } from './select-range';

// 定数
const LOGH = 'BFA10103G map body:';
const ZOOM_INIT = 18;

/**
 * 設備地図本体
 */
export default function SetubiMapBody({
  mainPole,
  poleList = [],
  setubiList = [],
  orderPoles,
  itemSelected = null,
  onItemSelect,
  onPoleListSelect,
  onSetubiAdd, onSetubiEdit, onSetubiDelete,
}) {

  // 地図参照
  const viewRef = React.useRef();
  const layersRef = React.useRef();
  const selectRef = React.useRef();
  const handlesRef = React.useRef();

  // 架設設備マップ
  const genericMap = useSelector((state) => state.master.genericMap);
  const setubiMap = React.useMemo(() => new Map(
    genericMap?.kasetu_setubi_cd?.map((gen) => ([ gen.cd, gen.cdName ]))
  ), [genericMap]);

  // 地図に渡すプロパティ (最新の値を ref 経由で渡す)
  const propValues = React.useRef();
  React.useEffect(() => {
    propValues.current = { itemSelected, onItemSelect, onPoleListSelect };
  });

  // ポップアップ
  const polePopup = usePopupPortal(popup.polePopupTitle);

  // 地図ズーム設定 (マウスホイール拡大/縮小の
  const wheelZoom = useSelector((state) => state.setting.wheelZoom);

  // 電柱選択モード
  const [ selectMode, setSelectMode ] = React.useState(null);

  //
  // 初期化処理
  //
  const handleReady = React.useCallback((event) => {
    console.debug(LOGH, '地図初期化');

    // ビュー参照
    const view = event.target?.view;
    viewRef.current = view;

    // ズーム初期値
    view.zoom = ZOOM_INIT;
    view.popup = mapUtils.popupConfig();   // ポップアップ設定
    view.popupEnabled = false;             // 自動ポップアップ無効
    enableWheelZoom(view, wheelZoom);      // ホイール動作設定

    // 描画レイヤー作成
    const layers = {
      pole:   new GraphicsLayer({ title: '電柱一覧' }),
      setubi: new GraphicsLayer({ title: '設備一覧' }),

      // 地図電柱マスタ
      master: new FeatureLayer({
        title: '電柱マスタ',
        portalItem: { id: appConfig.gis.masterLayer },
        popupEnabled: false,
        popupTemplate: polePopup.template,
        outFields: ['*'],
      }),
    };
    layersRef.current = layers;

    // レイヤー登録
    view.map.add(layers.master);
    view.map.add(layers.pole);
    view.map.add(layers.setubi);
    view.whenLayerView(layers.master).then((masterView) => {

      // 電柱マスタのレイヤービュー
      layers.masterView = masterView;
    });

    // ハンドラー管理
    if (!handlesRef.current) {
      handlesRef.current = new Handles();
    }
    const handles = handlesRef.current;
    handles.removeAll();

    // 範囲選択処理設定
    selectRef.current = rangeSelectSketchModel(view);
    const selectHandle = selectRef.current
        .on('create', (event) => rangeSelectHandler({
          event,
          sketch: selectRef.current,
          layers: layersRef.current,
          propValues, setSelectMode
        }));
    handles.add(selectHandle);

    // 電柱一覧描画
    drawPoles(layers.pole, polePopup.template, poleList);

    // 設備一覧描画
    drawSetubi(layers.setubi, polePopup.template, orderPoles);

    // 代表電柱を中央に表示
    const center = [ mainPole.longitude, mainPole.latitude ];
    view.goTo({ center })
        .catch((err) => console.info(LOGH, err.message));
  }, []);

  //
  // 終了処理 (アンマウント)
  //
  React.useEffect(() => {
    return () => {
      console.debug(LOGH, '地図終了');

      // 登録済のハンドラを全て削除
      handlesRef.current?.removeAll();
      handlesRef.current = null;
    }
  }, []);

  //----------------------------------------------------------------------------
  // 状態変更
  //----------------------------------------------------------------------------
  //
  // 電柱一覧変更
  //
  React.useEffect(() => {
    const view = viewRef.current;
    if (view) {
      console.debug(LOGH, '電柱一覧変更', poleList);

      // 電柱描画
      const layers = layersRef.current?.pole;
      drawPoles(layers, polePopup.template, poleList);

      // 表示範囲更新
      mapUtils.showAll(view, poleList);
    }
  }, [poleList]);

  //
  // 設備一覧変更
  //
  React.useEffect(() => {
    const view = viewRef.current;
    if (view) {
      console.debug(LOGH, '設備一覧変更', orderPoles.values());

      // 設備描画
      const layers = layersRef.current;
      drawSetubi(layers.setubi, polePopup.template, orderPoles);
      view.goTo(view.extent)  // 強制描画
          .catch((err) => console.info(LOGH, err.message));
    }
  }, [orderPoles]);

  //
  // 選択電柱変更
  //
  React.useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    // 選択項目変更 - 電柱選択
    if (itemSelected?.pole) {
      const poleSelected = itemSelected.pole;
      console.debug(LOGH, 'Pole selected', poleSelected);

      // 選択電柱ポップアップ
      const layers = layersRef.current;
      popup.openPolePopup(view, layers, poleSelected);
      return;
    }

    // 選択項目変更 - 選択解除
    popup.closePopup(view, layersRef.current);
  }, [itemSelected]);

  //----------------------------------------------------------------------------
  // イベント
  //----------------------------------------------------------------------------
  //
  // 地図クリック
  //
  const handleViewClick = React.useCallback(async (event) => {
    const { itemSelected, onItemSelect } = propValues.current;
    const view = event.target.view;
    const layers = layersRef.current;

    // ヒットテスト
    const hit = await view.hitTest(event.detail, {
      include: [ layers.setubi, layers.pole ],
    });
    for (const result of hit.results) {
      const graphic = result.graphic;
      const type = graphic?.attributes?.type;

      // 結果判定 - 電柱/設備
      if (type === 'pole' || type === 'setubi') {
        const pole = graphic.attributes.pole;
        console.info(LOGH, '電柱選択',
            `type=${type}`, PoleItem.toString(pole));

        // 選択電柱を更新
        const poleSelected = itemSelected?.pole;
        if (!poleSelected?.same(pole)) {
          onItemSelect(new PoleItem(pole), { from: 'map' });
        }

        // ポップアップ表示
        view.openPopup({
          location: graphic.geometry,
          features: [ graphic ],
        });
        return;
      }
    }

    // 地図電柱マスタ検索
    if (layers.masterView) {
      const query = layers.masterView.createQuery();
      query.geometry = event.detail.mapPoint;
      query.distance = 10 * view.resolution;

      // レイヤー検索
      const result = await layers.masterView.queryFeatures(query);
      for (const feature of result.features) {
        const pole = feature.attributes;

        // 結果判定 - 電柱
        if (pole.areaCode && pole.lineCode && pole.poleNo) {
          console.info(LOGH, '電柱選択',
            `type=master`, PoleItem.toString(pole));

          // 選択電柱を更新
          const poleSelected = itemSelected?.pole;
          if (!poleSelected?.same(pole)) {
            onItemSelect(new PoleItem(pole), { from: 'map' });
          }

          // ポップアップ表示
          view.openPopup({
            location: feature.geometry,
            features: [ feature ],
          });
          return;
        }
      }
    }

    // ポップアップ非表示
    popup.closePopup(view, layersRef.current);
    itemSelected && onItemSelect(null);
  }, []);

  //
  // 単一選択ボタン処理
  //
  const handleSelectSingle = React.useCallback(() => {

    // 範囲選択中なら取消
    selectRef.current?.cancel();
    setSelectMode(null);
  }, [])

  //
  // 範囲選択ボタン処理
  //
  const handleSelectRectangle = React.useCallback(() => {
    onItemSelect(null);   // 選択解除

    // 範囲選択モード開始
    selectRef.current?.cancel();
    selectRef.current?.create('rectangle', { mode: 'click' });
    setSelectMode('rectangle')
  }, [onItemSelect]);

  //
  // なげなわ選択ボタン処理
  //
  const handleSelectPolygon = React.useCallback(() => {
    onItemSelect(null);   // 選択解除

    // 範囲選択モード開始
    selectRef.current?.cancel();
    selectRef.current?.create('polygon', { mode: 'click' });
    setSelectMode('polygon')
  }, [onItemSelect]);

  //
  // 代表電柱を表示
  //
  const handleShowHome = React.useCallback(() => {
    const center = [ mainPole.longitude, mainPole.latitude ];
    const target = {
      center: center,    // 代表電柱を中央
      zoom: ZOOM_INIT,   // ズームも初期化
    };
    viewRef.current?.goTo(target, mapUtils.GOTO_OPTIONS)
        .catch((err) => console.info(LOGH, err.message));

    // 代表電柱を選択(ポップアップ)
    onItemSelect(mainPole, { from: 'map' });
  }, [mainPole, onItemSelect]);

  //
  // 電柱一覧を全て表示
  //
  const handleShowPoleList = React.useCallback(() => {
    mapUtils.showAll(viewRef.current, poleList);
  }, [poleList]);

  //
  // 設備一覧を全て表示
  //
  const handleShowSetubiList = React.useCallback(() => {
    mapUtils.showAll(viewRef.current, Array.from(orderPoles.values()));
  }, [orderPoles]);

  //----------------------------------------------------------------------------
  // 描画
  //----------------------------------------------------------------------------
  return (
    <Box sx={{
        height: '100%',
        border: 1,
        borderColor: 'grey.400',
      }}
    >
      <ArcgisMap
        basemap='arcgis/topographic'
        onArcgisViewReadyChange={handleReady}
        onArcgisViewClick={handleViewClick}
      >
        {/* アクションバー */}
        <MapActionBar slot="top-left">

          {/* 電柱選択ツール */}
          <CalciteActionGroup>

            {/* 単一選択 */}
            <MapActionButton
              icon="cursor"
              text="電柱選択"
              title="電柱選択"
              active={!selectMode}
              onClick={handleSelectSingle}
            />

            {/* 範囲選択 */}
            <MapActionButton
              icon="cursor-marquee"
              text="範囲選択"
              title="範囲選択"
              active={selectMode === 'rectangle'}
              onClick={handleSelectRectangle}
            />

            {/* なげなわ選択 */}
            <MapActionButton
              icon="lasso-select"
              text="なげなわ選択"
              title="なげなわ選択"
              active={selectMode === 'polygon'}
              onClick={handleSelectPolygon}
            />
          </CalciteActionGroup>

          {/* 電柱表示ツール */}
          <CalciteActionGroup>

            {/* 電柱一覧ボタン */}
            <MapActionButton
              icon="content-side-by-side"
              text="検索電柱表示"
              title="検索した電柱の一覧を地図に表示します。"
              onClick={handleShowPoleList}
            />

            {/* 設備一覧ボタン */}
            <MapActionButton
              icon="coded-value-domain"
              text="申込電柱表示"
              title="申込登録中の電柱を地図に表示します。"
              onClick={handleShowSetubiList}
            />

            {/* ホームボタン */}
            <MapActionButton
              icon="home"
              text="代表電柱表示"
              title="代表電柱を表示します。"
              onClick={handleShowHome}
            />
          </CalciteActionGroup>

          {/* ボタンレイアウト切替 */}
          <CalciteActionGroup>
            <MapActionLayout />
          </CalciteActionGroup>
        </MapActionBar>

        {/* 検索ボックス */}
        <MapSearchButton slot="top-right" />

        {/* ズームボタン */}
        <MapZoomButton slot="top-right" />

        {/* ホイール設定ボタン */}
        <MapWheelSetting slot="top-right" viewRef={viewRef} />

        {/* コンパス */}
        <MapCompassButton slot="top-right" />

        {/* スケールバー */}
        <ArcgisScaleBar
          slot="bottom-right"
          barStyle="line" unit="metric"
        />

        {/* 電柱ポップアップ */}
        <PopupPortal container={polePopup.container}>
          <popup.PolePopupContent
            attribute={polePopup.attribute}
            setubiMap={setubiMap}
            setubiList={setubiList}
            onSetubiAdd={onSetubiAdd}
            onSetubiEdit={onSetubiEdit}
            onSetubiDelete={onSetubiDelete}
          />
        </PopupPortal>
      </ArcgisMap>
    </Box>
  );
}

// プロパティ
SetubiMapBody.propTypes = {
  mainPole:   PropTypes.object,             // 代表電柱
  poleList:   PropTypes.array,              // 電柱一覧
  setubiList: PropTypes.array,              // 設備リスト
  orderPoles: PropTypes.instanceOf(Map),    // 申込電柱一覧

  // 選択電柱
  itemSelected: PropTypes.object,

  // イベント
  onPoleListSelect: PropTypes.func.isRequired,   // 電柱一覧更新
  onItemSelect:     PropTypes.func.isRequired,   // 電柱選択
  onSetubiAdd:      PropTypes.func.isRequired,   // 設備追加
  onSetubiEdit:     PropTypes.func.isRequired,   // 設備編集
  onSetubiDelete:   PropTypes.func.isRequired,   // 設備削除
};
